import { AimOutlined, ReloadOutlined, RightOutlined, ToolOutlined } from '@ant-design/icons'
import { Button, Drawer, Switch, Tag } from 'antd'
import { useMemo, useState, useSyncExternalStore, type ReactNode } from 'react'
import { env } from '@/config/env.config'
import { useAuthStore, type ApiMenuItem } from '@/stores/auth.store'
import type { PermissionDebugEntry } from './permission-debug-store'
import { permissionDebugStore } from './permission-debug-store'
import { flashPermissionDebugTarget } from './permission-debug-target'

function renderMenuTree(items: ApiMenuItem[] | null | undefined): ReactNode {
  if (!items?.length) {
    return <div className="rounded bg-gray-50 px-3 py-4 text-center text-xs text-gray-400">暂无菜单数据</div>
  }

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={`${item.path}-${item.meta.title}`} className="rounded border border-gray-100 bg-white px-3 py-3">
          <div className="text-sm font-medium text-gray-700">{item.meta.title}</div>
          <div className="mt-1 text-xs text-gray-400">{item.path || '-'}</div>
          {item.children?.length ? (
            <div className="mt-3 border-l border-orange-100 pl-3">{renderMenuTree(item.children)}</div>
          ) : null}
        </div>
      ))}
    </div>
  )
}

function normalizePath(path: string): string {
  const pathname = path.split(/[?#]/)[0] || '/'
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
}

function findMenuTrail(items: ApiMenuItem[] | null | undefined, pathname: string, trail: ApiMenuItem[] = []): ApiMenuItem[] | null {
  for (const item of items ?? []) {
    const nextTrail = [...trail, item]
    if (normalizePath(item.path) === normalizePath(pathname)) return nextTrail
    const matchedChildren = findMenuTrail(item.children, pathname, nextTrail)
    if (matchedChildren) return matchedChildren
  }
  return null
}

interface PermissionOperationSummary {
  entry: PermissionDebugEntry
  instanceCount: number
}

interface PermissionLocationGroup {
  location: string
  operations: PermissionOperationSummary[]
}

function summarizePermissionEntries(entries: PermissionDebugEntry[]): PermissionLocationGroup[] {
  const locations = new Map<string, Map<string, PermissionOperationSummary>>()

  entries.forEach((entry) => {
    const location = entry.location ?? '未标注业务位置'
    const operationKey = JSON.stringify([
      entry.action,
      entry.permission,
      entry.allowed,
      entry.mode,
      entry.source,
      entry.sourceLocation,
    ])
    const operations = locations.get(location) ?? new Map<string, PermissionOperationSummary>()
    const existing = operations.get(operationKey)

    if (existing) {
      existing.instanceCount += 1
    } else {
      operations.set(operationKey, { entry, instanceCount: 1 })
    }
    locations.set(location, operations)
  })

  return Array.from(locations, ([location, operations]) => ({
    location,
    operations: Array.from(operations.values()),
  }))
}

function formatSourceLocation(sourceLocation?: string): string {
  if (!sourceLocation) return '来源待补充'

  const sourceStart = sourceLocation.indexOf('/src/')
  return sourceStart >= 0 ? sourceLocation.slice(sourceStart + 1).split(/[?#]/)[0] : sourceLocation
}

function PermissionOperationList({ operations, onLocateTarget }: {
  operations: PermissionOperationSummary[]
  onLocateTarget?: () => void
}) {
  return (
    <div className="divide-y divide-gray-100">
      {operations.map(({ entry, instanceCount }) => {
        const actionLabel = entry.action ?? entry.permission ?? '未命名操作'
        const canLocate = entry.allowed || entry.mode === 'disable'
        const canOverride = entry.source === 'permission' && Boolean(entry.permission)
        return (
          <div key={entry.id} className="py-2 first:pt-0 last:pb-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 text-sm font-medium text-gray-700">{actionLabel}</div>
              <div className="flex shrink-0 items-center gap-1">
                {instanceCount > 1 ? <Tag className="m-0">{instanceCount} 个实例</Tag> : null}
                {canOverride ? (
                  <Switch
                    size="small"
                    aria-label={`${actionLabel}权限开关`}
                    checked={entry.allowed}
                    checkedChildren="开"
                    unCheckedChildren="关"
                    onChange={(checked) => {
                      permissionDebugStore.setPermissionOverride(entry.systemCode, entry.permission!, checked)
                    }}
                  />
                ) : (
                  <Tag color={entry.allowed ? 'success' : 'error'} className="m-0">
                    {entry.allowed ? '显示' : entry.mode === 'hide' ? '隐藏' : '禁用'}
                  </Tag>
                )}
                <Button
                  type="text"
                  size="small"
                  title="定位页面元素"
                  aria-label={`定位${actionLabel}`}
                  icon={<AimOutlined />}
                  disabled={!canLocate}
                  onClick={() => {
                    onLocateTarget?.()
                    window.setTimeout(() => flashPermissionDebugTarget(entry.id), 240)
                  }}
                />
              </div>
            </div>
            <div className="mt-1 break-all text-xs text-gray-500">
              {entry.permission ? `权限：${entry.permission}` : '判定：显式条件'}
            </div>
            <div className="mt-1 break-all font-mono text-xs text-gray-400">
              源码：{formatSourceLocation(entry.sourceLocation)}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PermissionLocationGroups({ groups, onLocateTarget }: {
  groups: PermissionLocationGroup[]
  onLocateTarget?: () => void
}) {
  return (
    <div className="divide-y divide-gray-100" data-testid="permission-location-group">
      {groups.map(({ location, operations }) => (
        <section key={location} className="py-3 first:pt-1 last:pb-1">
          <div className="mb-2 flex items-start justify-between gap-2">
            <div className="min-w-0 break-words text-xs font-medium leading-5 text-gray-600">
              {location.split('>').map((segment) => segment.trim()).filter(Boolean).join(' / ')}
            </div>
            <Tag className="m-0 shrink-0">{operations.length}</Tag>
          </div>
          <PermissionOperationList operations={operations} onLocateTarget={onLocateTarget} />
        </section>
      ))}
    </div>
  )
}

interface MenuPermissionInspectorProps {
  permissionOutlineEnabled?: boolean
  setPermissionOutlineEnabled?: (enabled: boolean) => void
  onLocateTarget?: () => void
}

export default function MenuPermissionInspector({
  permissionOutlineEnabled = false,
  setPermissionOutlineEnabled = () => {},
  onLocateTarget,
}: MenuPermissionInspectorProps) {
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedSystemCode, setSelectedSystemCode] = useState<string | null>(null)
  const [detailView, setDetailView] = useState<'structured' | 'json'>('structured')
  const mockMenuEnabled = useAuthStore((state) => state.mockMenuEnabled)
  const mockPermissionsEnabled = useAuthStore((state) => state.mockPermissionsEnabled)
  const setMockMenuEnabled = useAuthStore((state) => state.setMockMenuEnabled)
  const setMockPermissionsEnabled = useAuthStore((state) => state.setMockPermissionsEnabled)
  const permissionsMap = useAuthStore((state) => state.permissionsMap)
  const menuMap = useAuthStore((state) => state.menuMap)
  const permissionEntries = useSyncExternalStore(
    (listener) => permissionDebugStore.subscribe(listener),
    () => permissionDebugStore.getEntries(),
    () => permissionDebugStore.getEntries(),
  )
  const pathname = window.location.pathname
  const systems = useMemo(
    () => Array.from(new Set([...Object.keys(permissionsMap), ...Object.keys(menuMap)])),
    [menuMap, permissionsMap]
  )
  const currentEntries = permissionEntries.filter((entry) => normalizePath(entry.pathname) === normalizePath(pathname))
  const currentSystemCode = currentEntries[0]?.systemCode
    ?? systems.find((systemCode) => Boolean(findMenuTrail(menuMap[systemCode], pathname)))
    ?? systems[0]
    ?? null
  const currentMenuTrail = currentSystemCode ? findMenuTrail(menuMap[currentSystemCode], pathname) : null
  const currentMenuEntries = currentEntries.filter((entry) => entry.systemCode === currentSystemCode)
  const currentMenuLocationGroups = summarizePermissionEntries(currentMenuEntries)
  const currentMenuOperationCount = currentMenuLocationGroups.reduce((count, group) => count + group.operations.length, 0)
  const selectedSystemPermissions = selectedSystemCode ? permissionsMap[selectedSystemCode] ?? null : null
  const selectedSystemMenus = selectedSystemCode ? menuMap[selectedSystemCode] ?? [] : []
  const selectedSystemPayload = selectedSystemCode
    ? {
        systemCode: selectedSystemCode,
        permissions: selectedSystemPermissions,
        menuList: selectedSystemMenus,
      }
    : null
  const menuSourceLabel = mockMenuEnabled ? '本地 mock' : '正式本地菜单'
  const permissionSourceLabel = mockPermissionsEnabled ? '本地 mock' : '认证权限'

  const openSystemDetail = (systemCode: string) => {
    setSelectedSystemCode(systemCode)
    setDetailView('structured')
    setDetailOpen(true)
  }
  const closeSystemDetail = () => {
    setDetailOpen(false)
    setSelectedSystemCode(null)
    setDetailView('structured')
  }

  return (
    <>
      <section className="rounded bg-white p-3 shadow-sm">
        <div className="rounded-lg bg-orange-50 px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white text-xl text-orange-500 shadow-sm">
              <ToolOutlined />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-base font-semibold leading-6 text-gray-800">菜单&权限</span>
              <span className="mt-1 block text-sm leading-6 text-gray-500">当前菜单源与系统级权限数据查看入口</span>
              <span className="mt-3 block text-xs leading-5 text-gray-500">
                菜单：{menuSourceLabel} · 权限：{permissionSourceLabel} · 已加载 {systems.length} 个系统
              </span>
            </span>
            <span className="flex shrink-0 flex-col items-end gap-2 text-xs text-gray-500">
              <label className="flex items-center gap-2">
                <span>Mock菜单</span>
                <Switch
                  aria-label="Mock菜单"
                  checked={mockMenuEnabled}
                  disabled={!env.dev}
                  onChange={setMockMenuEnabled}
                />
              </label>
              <label className="flex items-center gap-2">
                <span>Mock权限</span>
                <Switch
                  aria-label="Mock权限"
                  checked={mockPermissionsEnabled}
                  disabled={!env.dev}
                  onChange={setMockPermissionsEnabled}
                />
              </label>
            </span>
          </div>

          <div className="mt-4 border-t border-orange-100 pt-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-medium text-gray-700">已加载系统配置</div>
              <div className="text-xs text-gray-400">{systems.length} 个</div>
            </div>
            {systems.length > 0 ? (
              <div className="space-y-2">
                {systems.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => openSystemDetail(code)}
                    className="flex w-full items-center justify-between gap-3 rounded-lg bg-white px-3 py-3 text-left transition-colors hover:bg-orange-100"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-700" title={code}>{code}</span>
                      <span className="mt-1 block text-xs text-gray-400">
                        {permissionsMap[code]?.permissions.length ?? 0} 权限 · {menuMap[code]?.length ?? 0} 菜单
                      </span>
                    </span>
                    <RightOutlined className="shrink-0 text-sm text-orange-400" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-lg bg-white px-3 py-4 text-center text-xs text-gray-400">暂无已加载系统配置</div>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-orange-100 pt-3">
            <span className="text-base font-semibold leading-6 text-gray-800">权限标识</span>
            <Switch
              aria-label="权限标识"
              checked={permissionOutlineEnabled}
              checkedChildren="开启"
              unCheckedChildren="关闭"
              onChange={setPermissionOutlineEnabled}
            />
          </div>

          <div className="mt-4 border-t border-orange-100 pt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-base font-semibold leading-6 text-gray-800">当前菜单&权限</div>
              <div className="flex items-center gap-1">
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined />}
                  aria-label="重置权限开关"
                  disabled={!permissionDebugStore.hasPermissionOverrides()}
                  onClick={() => permissionDebugStore.resetPermissionOverrides()}
                >
                  重置
                </Button>
                <Tag className="m-0">{currentMenuOperationCount} 项操作</Tag>
              </div>
            </div>
            <div className="space-y-1 text-xs leading-5 text-gray-500">
              <div className="break-all">当前路径：{pathname}</div>
              <div>当前系统：{currentSystemCode ?? '未识别'}</div>
              {currentMenuTrail ? (
                <div>当前菜单：{currentMenuTrail.map((item) => item.meta.title).join(' / ')}</div>
              ) : (
                <div>当前路由未匹配到已加载菜单配置</div>
              )}
            </div>
            {currentMenuEntries.length > currentMenuOperationCount ? (
              <div className="mt-2 text-xs text-gray-400">由 {currentMenuEntries.length} 个当前页组件实例汇总</div>
            ) : null}
            <div className="mt-3 space-y-2">
              {currentMenuLocationGroups.length > 0 ? (
                <div className="rounded bg-white px-3 py-2">
                  <PermissionLocationGroups groups={currentMenuLocationGroups} onLocateTarget={onLocateTarget} />
                </div>
              ) : (
                <div className="rounded bg-white px-3 py-3 text-xs text-gray-400">当前页面暂无已登记的权限操作</div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Drawer
        title={selectedSystemCode ?? '菜单&权限详情'}
        open={detailOpen}
        onClose={closeSystemDetail}
        placement="right"
        size={460}
        destroyOnHidden
        styles={{ body: { padding: 16, background: '#f7f8fa' } }}
      >
        <div className="space-y-4" data-c="MenuPermissionInspector">
          <section className="rounded bg-white p-1 shadow-sm">
            <div className="grid grid-cols-2 gap-1">
              <Button
                type="text"
                onClick={() => setDetailView('structured')}
                className={detailView === 'structured' ? '!bg-blue-50 !text-blue-600' : '!text-gray-500'}
                style={{ height: 'auto', padding: '10px 6px' }}
              >
                结构化
              </Button>
              <Button
                type="text"
                onClick={() => setDetailView('json')}
                className={detailView === 'json' ? '!bg-blue-50 !text-blue-600' : '!text-gray-500'}
                style={{ height: 'auto', padding: '10px 6px' }}
              >
                JSON
              </Button>
            </div>
          </section>

          {detailView === 'structured' ? (
            <div className="space-y-4">
              <section className="rounded bg-white p-3 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-800">权限</div>
                  <Tag>{selectedSystemPermissions?.permissions.length ?? 0}</Tag>
                </div>
                {selectedSystemPermissions?.permissions.length ? (
                  <div className="max-h-56 space-y-2 overflow-auto">
                    {selectedSystemPermissions.permissions.map((permission) => (
                      <div key={permission} className="rounded bg-gray-50 px-3 py-2 text-xs text-gray-600">{permission}</div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded bg-gray-50 px-3 py-4 text-center text-xs text-gray-400">暂无权限数据</div>
                )}
              </section>
              <section className="rounded bg-white p-3 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-800">菜单树</div>
                  <Tag>{selectedSystemMenus.length}</Tag>
                </div>
                {renderMenuTree(selectedSystemMenus)}
              </section>
            </div>
          ) : (
            <section className="rounded bg-slate-950 p-3 text-xs text-slate-100 shadow-sm">
              <pre className="overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(selectedSystemPayload, null, 2)}</pre>
            </section>
          )}
        </div>
      </Drawer>
    </>
  )
}
